import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  CpuChipIcon,
  ChartBarIcon,
  ArrowsRightLeftIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const Technology = () => {
  const { t } = useTranslation();

  const technologies = [
    {
      title: t('technology.core.hft.title'),
      description: t('technology.core.hft.description'),
      features: [
        t('technology.core.hft.features.architecture'),
        t('technology.core.hft.features.processing'),
        t('technology.core.hft.features.risk'),
        t('technology.core.hft.features.routing'),
      ],
      icon: CpuChipIcon,
    },
    {
      title: t('technology.core.pricing.title'),
      description: t('technology.core.pricing.description'),
      features: [
        t('technology.core.pricing.features.ml'),
        t('technology.core.pricing.features.adaptive'),
        t('technology.core.pricing.features.depth'),
        t('technology.core.pricing.features.volatility'),
      ],
      icon: ChartBarIcon,
    },
  ];

  const strategies = [
    {
      title: t('technology.strategies.arbitrage.title'),
      description: t('technology.strategies.arbitrage.description'),
      features: [
        t('technology.strategies.arbitrage.features.cross'),
        t('technology.strategies.arbitrage.features.spot'),
        t('technology.strategies.arbitrage.features.statistical'),
        t('technology.strategies.arbitrage.features.triangular'),
      ],
      icon: ArrowsRightLeftIcon,
    },
    {
      title: t('technology.strategies.hedging.title'),
      description: t('technology.strategies.hedging.description'),
      features: [
        t('technology.strategies.hedging.features.multi'),
        t('technology.strategies.hedging.features.dynamic'),
        t('technology.strategies.hedging.features.volatility'),
        t('technology.strategies.hedging.features.tail'),
      ],
      icon: ShieldCheckIcon,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('technology.meta.title')} - 9527.fi</title>
        <meta name="description" content={t('technology.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl font-bold mb-6">{t('technology.hero.title')}</h1>
            <p className="text-xl text-gray-200">
              {t('technology.hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Technology Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('technology.sections.core')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg"
              >
                <tech.icon className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-2xl font-bold mb-4">{tech.title}</h3>
                <p className="text-gray-600 mb-6">{tech.description}</p>
                <ul className="space-y-3">
                  {tech.features.map((feature) => (
                    <li key={feature} className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trading Strategies Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('technology.sections.strategies')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {strategies.map((strategy, index) => (
              <motion.div
                key={strategy.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg"
              >
                <strategy.icon className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-2xl font-bold mb-4">{strategy.title}</h3>
                <p className="text-gray-600 mb-6">{strategy.description}</p>
                <ul className="space-y-3">
                  {strategy.features.map((feature) => (
                    <li key={feature} className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('technology.sections.stack')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {['Go', 'Rust', 'Python', 'React', 'Kubernetes', 'Docker', 'PostgreSQL', 'Redis'].map((tech) => (
              <motion.div
                key={tech}
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <span className="text-xl font-semibold text-gray-800">{tech}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Technology; 